import React, { useEffect, useMemo, useState } from 'react';
import { usePdfViewportLogic } from '../hooks/usePdfViewportLogic';
import type { IPdfReaderState } from '../types';
import { PdfPage } from './PdfPage';

interface ViewportProps {
    state: IPdfReaderState;
    // biome-ignore lint/suspicious/noExplicitAny: controller is a loosely-typed legacy object
    controller: any;
    containerRef: React.RefObject<HTMLDivElement>;
    children?: React.ReactNode;
}

/**
 * PDF 视口组件（纯 UI 组件）
 *
 * 核心职责：
 * 1. 渲染 PDF 页面列表（垂直滚动模式：虚拟化渲染，水平翻页模式：单页渲染）
 * 2. 处理内容点击事件（切换工具栏）
 *
 * 业务逻辑已提取到 usePdfViewportLogic Hook 中
 */
export const PdfViewport = (props: ViewportProps) => {
    const { state, controller, containerRef, children } = props;
    const {
        totalPages,
        pdfDoc,
        scale,
        renderScale,
        pageNum,
        renderType,
        renderPixelRadio,
        screenState,
        pdfImages,
        renderTypeReady,
        enableCjkFix,
    } = state;
    const { registerTextLayer, unregisterTextLayer } = controller;

    // 使用业务逻辑 Hook
    const {
        wrapperRef,
        horizontalRef,
        visibleRange,
        shouldShowAllPages,
        totalContentHeight,
        handleContentClick,
        updatePageHeight,
        getPageTop,
        getPageHeight,
        scrollToPage,
    } = usePdfViewportLogic({ state, controller, containerRef });

    // 垂直滚动模式：追踪第一页的自然宽度（scale=1 时的像素宽）
    // 用于将 pdf-list-vertical 精确限定为画布宽度，避免容器宽度引起的多余横向滚动条
    const [naturalPageWidth, setNaturalPageWidth] = useState(0);
    useEffect(() => {
        if (!(pdfDoc && shouldShowAllPages)) return;
        let cancelled = false;
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        pdfDoc.getPage(1).then((page: any) => {
            if (cancelled) return;
            const vp = page.getViewport({ scale: 1 });
            setNaturalPageWidth(vp.width);
        });
        return () => {
            cancelled = true;
        };
    }, [pdfDoc, shouldShowAllPages]);

    // 当前缩放下画布的视觉宽度；未就绪时回退到 undefined（保持 100% 宽）
    const listWidth = naturalPageWidth > 0 && scale > 0 ? naturalPageWidth * scale : undefined;

    // 内部书签跳转：1-based 页码 → 0-based index
    const handleInternalLinkClick = React.useCallback(
        (targetPageNum: number) => {
            scrollToPage(targetPageNum - 1, 'smooth');
        },
        [scrollToPage]
    );

    // 将"当前可见页集合"同步给 usePdfSearch（用于缩放时只重渲染视口页）
    useEffect(() => {
        const updateVisiblePages = controller?.updateVisiblePages as undefined | ((pages: number[]) => void);
        if (!updateVisiblePages) {
            return;
        }
        if (!pdfDoc || totalPages <= 0) {
            updateVisiblePages([]);
            return;
        }

        if (shouldShowAllPages) {
            const pages: number[] = [];
            for (let i = visibleRange.start; i < visibleRange.end; i++) {
                const pageNum = i + 1;
                if (pageNum >= 1 && pageNum <= totalPages) {
                    pages.push(pageNum);
                }
            }
            updateVisiblePages(pages);
            return;
        }

        if (pageNum?.value > 0) {
            updateVisiblePages([pageNum.value]);
        } else {
            updateVisiblePages([]);
        }
    }, [controller, pdfDoc, totalPages, shouldShowAllPages, visibleRange.start, visibleRange.end, pageNum?.value]);

    // ========== 页面列表渲染 ==========
    const renderList = useMemo(() => {
        const shouldRender = pdfDoc && totalPages > 0 && renderTypeReady;
        if (!shouldRender) {
            return null;
        }

        if (shouldShowAllPages) {
            const start = visibleRange.start;
            const end = visibleRange.end;

            const items: React.ReactNode[] = [];
            for (let index = start; index < end; index++) {
                const top = getPageTop(index);
                const height = getPageHeight(index);
                items.push(
                    <section
                        key={index}
                        aria-label={`Page-${index + 1}`}
                        style={{
                            position: 'absolute',
                            top: `${top}px`,
                            left: 0,
                            width: '100%',
                            height: height > 0 ? `${height}px` : 'auto',
                            display: 'flex',
                            alignItems: 'flex-start', // center 会在 cssTransformRatio>1 时产生偏移间距，改为 flex-start
                            overflow: 'visible', // 允许横向内容溢出，横向滚动条由 .doc-container 承载
                        }}
                    >
                        <div className="pdf-viewport-list-item" style={{ margin: '0 auto' }}>
                            <PdfPage
                                pageNum={index + 1}
                                pdfDoc={pdfDoc}
                                scale={scale}
                                renderScale={renderScale}
                                screenState={screenState}
                                registerTextLayer={registerTextLayer}
                                unregisterTextLayer={unregisterTextLayer}
                                renderType={renderType}
                                renderPixelRadio={renderPixelRadio}
                                pdfImages={pdfImages}
                                onMeasure={updatePageHeight}
                                pageIndex={index}
                                onInternalLinkClick={handleInternalLinkClick}
                                enableCjkFix={enableCjkFix}
                            />
                        </div>
                    </section>
                );
            }
            return (
                <div style={{ position: 'relative', width: '100%', height: `${totalContentHeight}px` }}>{items}</div>
            );
        }

        return null;
    }, [
        pdfDoc,
        totalPages,
        renderTypeReady,
        visibleRange,
        scale,
        renderScale,
        screenState,
        registerTextLayer,
        unregisterTextLayer,
        renderType,
        renderPixelRadio,
        pdfImages,
        shouldShowAllPages,
        totalContentHeight,
        getPageTop,
        getPageHeight,
        updatePageHeight,
        handleInternalLinkClick,
        enableCjkFix,
    ]);

    return (
        <div className="pdf-content" onClick={handleContentClick} style={{ position: 'relative' }}>
            <div
                className="pdf-content-wrap"
                style={{
                    // 全屏：固定高度撑满容器，overflow 由 .doc-container.full 统一承载
                    // 非全屏：高度随内容自适应，不创建内部滚动容器（由外层页面滚动条处理）
                    height: screenState === 'full' ? '100%' : 'auto',
                    overflow: 'visible',
                }}
            >
                {shouldShowAllPages ? (
                    <div
                        className="pdf-list-vertical"
                        ref={wrapperRef}
                        style={listWidth !== undefined ? { width: listWidth, margin: '0 auto' } : undefined}
                    >
                        {renderList}
                        {children}
                    </div>
                ) : (
                    <div
                        className="pdf-list-horizontal"
                        ref={horizontalRef}
                        data-page={pageNum?.value}
                        style={{
                            width: '100%',
                            // 全屏：撑满高度（水平翻页模式，纵向溢出由 overflow-x:auto 触发的 auto 处理）
                            // 非全屏：高度随内容自适应 —— height:auto 使纵向不产生溢出，
                            //         overflow-x:auto 仅在横向超宽时出现滚动条
                            height: screenState === 'full' ? '100%' : 'auto',
                            display: 'flex',
                            // overflow-x:auto：放大超出容器宽度时显示横向滚动条
                            // overflow-y 由浏览器 coerce 为 auto，但非全屏 height:auto 不产生纵向滚动条
                            overflowX: 'auto',
                            position: 'relative',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <div
                            style={{
                                margin: 'auto',
                                padding: screenState === 'full' ? '20px 0' : '0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            {(() => {
                                const canRender = pdfDoc && pageNum?.value > 0 && renderTypeReady;
                                if (canRender) {
                                    return (
                                        <PdfPage
                                            pageNum={pageNum.value}
                                            pdfDoc={pdfDoc}
                                            scale={scale}
                                            renderScale={renderScale}
                                            screenState={screenState}
                                            registerTextLayer={registerTextLayer}
                                            unregisterTextLayer={unregisterTextLayer}
                                            renderType={renderType}
                                            renderPixelRadio={renderPixelRadio}
                                            pdfImages={pdfImages}
                                            onInternalLinkClick={handleInternalLinkClick}
                                            enableCjkFix={enableCjkFix}
                                        />
                                    );
                                }
                                return null;
                            })()}
                        </div>
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
};
